import { modalStore, type ConfirmModalPayload } from './modal-store';

type ConfirmOptions = Omit<ConfirmModalPayload, 'onResolve'>;

/**
 * Imperative, promise-based confirmation. Resolves `true` if the user confirms,
 * `false` if they cancel or dismiss. Usable from anywhere (event handlers,
 * mutations, features) without rendering a dialog inline.
 *
 * Lives in `shared/lib` (only depends on the modal store, also shared) so any
 * layer — including features — can call it without an upward `app` import.
 *
 * ```ts
 * if (await confirmDialog({ title: 'Cancel order?', destructive: true })) {
 *   cancel();
 * }
 * ```
 */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const settle = (value: boolean): void => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    modalStore.open('confirm', { ...options, onResolve: settle });
  });
}
